import { FC, useCallback, useState } from 'react';
import EntityDrawerContainer from '../entity-drawer-layout/EntityDrawerContainer';
import EntityDrawerHeader from '../entity-drawer-layout/EntityDrawerHeader';
import EditRecurringEvent from './EditRecurringEvent';
import RecurringEventHistory from './components/RecurringEventHistory';
import { Box, Tab, Tabs } from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import UpdatedStyleWrapper from '../updated-style-wrapper';
import { openConfirmPopup } from '../../store/confirmPopupSlice';
import { useAppDispatch } from '../../hooks/redux';
import useShowSnackbar from '../../hooks/useShowSnackbar';
import appointmentService from '../../services/AppointmentService';
import { startSubmitting, stopSubmitting } from '../../store/slices/SubmittingSlice';
import { SnackBarTypes } from '../../store/snackbarReducer';
import useCalendarRestrictions from '../../views/new-calendar/hooks/use-calendar-restrictions';

type RecurringEventDetailsProps = {
    id: number | string;
    entry_date: string;
    onClose: () => void;
};

const RecurringEventDetails: FC<RecurringEventDetailsProps> = ({ id, entry_date, onClose }) => {
    const dispatch = useAppDispatch();
    const { showSnackbar } = useShowSnackbar();
    const [tab, setTab] = useState<'details' | 'attendees' | 'history'>('details');

    const { canDelete } = useCalendarRestrictions();

    const [deleteEvent] = appointmentService.useDeleteRecurringEventMutation();

    const handleDelete = useCallback(() => {
        dispatch(startSubmitting());

        deleteEvent(id)
            .unwrap()
            .then(() => {
                showSnackbar({
                    message: 'Event deleted.',
                    alertSeverity: SnackBarTypes.Success
                });
                onClose();
            })
            .catch((e) => {
                showSnackbar({
                    message: e.message || 'Event was not deleted.',
                    alertSeverity: SnackBarTypes.Error
                });
            })
            .finally(() => dispatch(stopSubmitting()));
    }, [deleteEvent, dispatch, id, onClose, showSnackbar]);

    const onDelete = useCallback(() => {
        dispatch(
            openConfirmPopup({
                onConfirm: handleDelete,
                confirmText: `Delete`,
                title: 'Delete Event',
                text: 'Are you sure you want to delete this event?'
            })
        );
    }, [dispatch, handleDelete]);

    return (
        <UpdatedStyleWrapper>
            <EntityDrawerContainer>
                <EntityDrawerHeader
                    title="Event Details"
                    onClose={onClose}
                    onDelete={canDelete ? onDelete : undefined}
                    entityName="Event"
                />
                <Box
                    sx={{
                        width: '100%',
                        '& .MuiTab-root': { minWidth: '73px', minHeight: '49px', py: 1, px: 2, flexGrow: 1 },
                        '& .MuiTabs-root': {
                            minHeight: '50px'
                        }
                    }}
                >
                    <Tabs variant="scrollable" scrollButtons="auto" orientation="horizontal" onChange={(_, v) => setTab(v)} value={tab}>
                        <Tab value="details" icon={<AssignmentOutlinedIcon />} iconPosition="start" />
                        <Tab value="attendees" icon={<AssignmentIndOutlinedIcon />} iconPosition="start" />
                        <Tab value="history" icon={<UpdateOutlinedIcon />} iconPosition="start" />
                    </Tabs>
                </Box>
                {tab === 'details' ? <EditRecurringEvent id={id} entry_date={entry_date} onClose={onClose} /> : null}
                {tab === 'attendees' ? <EditRecurringEvent id={id} entry_date={entry_date} onClose={onClose} customersOnly /> : null}
                {tab === 'history' ? <RecurringEventHistory id={id} /> : null}
            </EntityDrawerContainer>
        </UpdatedStyleWrapper>
    );
};

export default RecurringEventDetails;
